import { summarizeBundle20ExitEnforceDetail } from "./bundle20ExitEnforce";
import type { CycleOperatorSummary, DashboardData } from "./types";

export type FlatReadinessTone = "green" | "blue" | "amber" | "red";

export interface FlatReadinessSummary {
  tone: FlatReadinessTone;
  title: string;
  detail: string;
  facts: string[];
}

export function selectFlatReadinessOverviewFact(summary: FlatReadinessSummary) {
  const exitFlowFact =
    summary.facts.find((fact) => fact.startsWith("exit 미분류")) ??
    summary.facts.find((fact) => fact.startsWith("hard-exit"));
  const exitPlanFact = summary.facts.find((fact) => fact.startsWith("exitPlan"));
  if (exitFlowFact && exitPlanFact) return `${exitFlowFact} · ${exitPlanFact}`;
  return exitFlowFact ?? exitPlanFact ?? summary.detail;
}

const WEAK_SOURCE_CLASSES = new Set(["chatter", "context_only", "stale", "inaccessible"]);
type AlertCandidate = NonNullable<DashboardData["alertCandidates"]>["alerts"][number];
type CollabNote = NonNullable<DashboardData["collaborationNotes"]>["notes"][number];

function sourceClassKorean(sourceClass: string) {
  if (sourceClass === "official") return "공식";
  if (sourceClass === "original") return "원문";
  if (sourceClass === "relay") return "전달";
  if (sourceClass === "chatter") return "잡음";
  if (sourceClass === "context_only") return "참고";
  if (sourceClass === "stale") return "낡음";
  if (sourceClass === "inaccessible") return "접근불가";
  return sourceClass;
}

function compactSourceClasses(classes: string[]) {
  if (!classes.length) return "품질 미확인";
  const counts = classes.reduce<Record<string, number>>((acc, sourceClass) => {
    acc[sourceClass] = (acc[sourceClass] ?? 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, 3)
    .map(([sourceClass, count]) => `${sourceClassKorean(sourceClass)} ${count}`)
    .join(" · ");
}

function sourceTypeKorean(sourceType: string | undefined) {
  if (sourceType === "x") return "X";
  if (sourceType === "telegram") return "텔레그램";
  if (sourceType === "rss") return "뉴스";
  if (sourceType === "api") return "거래소";
  if (sourceType === "websocket") return "시장";
  if (sourceType === "manual") return "수동";
  return "소식";
}

function compactText(text: string | undefined, maxLength = 140) {
  const compacted = text
    ?.replace(/https?:\/\/\S+/g, "")
    .replace(/(?:\.{3,}|…)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!compacted) return undefined;
  if (compacted.length > maxLength) return undefined;
  return compacted;
}

function compactEvidenceCategory(text: string | undefined) {
  const lower =
    text
      ?.replace(/https?:\/\/\S+/g, "")
      .replace(/(?:\.{3,}|…)/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase() ?? "";
  const reasons: string[] = [];
  if (/price impact contradicts|contradict/.test(lower)) reasons.push("가격 반응 충돌");
  if (/independent confirmation|independent source|corroboration/.test(lower))
    reasons.push("독립 확인 부족");
  if (
    reasons.includes("독립 확인 부족") === false &&
    /market confirmation|same-symbol market confirmation|market reaction/.test(lower)
  )
    reasons.push("시장 확인 부족");
  if (
    reasons.includes("독립 확인 부족") === false &&
    /source quality|source confirmation|weak source|realized source reliability/.test(lower)
  )
    reasons.push("출처 품질 보강 필요");
  if (/exit plan|exitplan|stoploss|timestop|maxadverse/.test(lower))
    reasons.push("exitPlan 보강 필요");
  return reasons.length ? [...new Set(reasons)].slice(0, 3).join(" · ") : undefined;
}

function alertNewsLine(alert: AlertCandidate | undefined) {
  if (!alert) return undefined;
  const asset = alert.assets[0] ?? "시장";
  const source = sourceTypeKorean(alert.sourceType);
  const raw = alert.rawTextRedacted ?? alert.normalizedText;
  const body = compactText(raw) ?? compactEvidenceCategory(raw);
  const fallback = `${alert.catalystType} · ${alert.sentiment}`;
  return `${asset} ${source}: ${body ?? fallback}`;
}

function noteNewsLine(note: CollabNote | undefined) {
  if (!note) return undefined;
  const asset = note.entryContext?.assets?.[0] ?? note.positionSnapshot?.symbol ?? "시장";
  const source = sourceTypeKorean(note.entryContext?.sourceType);
  const reason = compactText(note.verdict?.reason) ?? compactEvidenceCategory(note.verdict?.reason);
  return reason ? `${asset} ${source}: ${reason}` : undefined;
}

function learningReviewFact(data: DashboardData) {
  const fromSummary = data.collaborationNotes?.closedAwaitingCeoReviewCount;
  const pending =
    typeof fromSummary === "number"
      ? fromSummary
      : (data.collaborationNotes?.notes ?? []).filter(
          (note) => note.status === "closed" && (note.ceoReview ?? []).length === 0,
        ).length;
  return pending > 0 ? `학습 리뷰 대기 ${pending}건` : undefined;
}

function appendOptionalFact(facts: string[], fact: string | undefined) {
  return fact ? [...facts, fact] : facts;
}

function appendOperationalFacts(facts: string[], ...optionalFacts: Array<string | undefined>) {
  return optionalFacts.reduce(appendOptionalFact, facts);
}

type EntryObservability = NonNullable<DashboardData["entryObservability"]>;
type ExitPlanQuality = NonNullable<EntryObservability["exitPlanQuality"]>;
type ExitPlanWindow = NonNullable<ExitPlanQuality["windows"]>[string];

function exitPlanWindowLabel(key: string, window: ExitPlanWindow, includePrefix = true) {
  const prefix = includePrefix ? "exitPlan " : "";
  const gaps = Math.max(window.zeroStructuredFieldCount, window.missingExitPlan);
  if (gaps > 0) return `${prefix}${key} 0필드 ${gaps}건`;
  return `${prefix}${key} 정상 ${window.applicableEntryDecisions}건`;
}

function exitPlanQualityFact(data: DashboardData) {
  const quality = data.entryObservability?.exitPlanQuality;
  if (!quality || quality.applicableEntryDecisions <= 0) return undefined;
  const windows = quality.windows ?? {};
  const currentKey = ["1h", "6h", "24h"].find(
    (key) => (windows[key]?.applicableEntryDecisions ?? 0) > 0,
  );
  if (currentKey) {
    const current = windows[currentKey];
    const day = windows["24h"];
    const labels = [exitPlanWindowLabel(currentKey, current)];
    if (
      day &&
      currentKey !== "24h" &&
      Math.max(day.zeroStructuredFieldCount, day.missingExitPlan) >
        Math.max(current.zeroStructuredFieldCount, current.missingExitPlan)
    ) {
      labels.push(exitPlanWindowLabel("24h", day, false));
    }
    return labels.join(" · ");
  }
  if (quality.zeroStructuredFieldCount > 0)
    return `exitPlan 0필드 ${quality.zeroStructuredFieldCount}건`;
  if (quality.missingExitPlan > 0) return `exitPlan 누락 ${quality.missingExitPlan}건`;
  return `exitPlan 구조 중앙값 ${quality.structuredFieldCountP50}개`;
}

function bundle20ExitEnforceFact(cycle?: CycleOperatorSummary | null) {
  return summarizeBundle20ExitEnforceDetail(cycle) ?? undefined;
}

function exitWorkflowFact(data: DashboardData) {
  const exitWorkflow = data.entryObservability?.exitWorkflow;
  if (!exitWorkflow || exitWorkflow.terminalExitNotes <= 0) return undefined;
  if (exitWorkflow.unclassifiedExecutionExitCount > 0) {
    return `exit 미분류 ${exitWorkflow.unclassifiedExecutionExitCount}건 확인 필요`;
  }
  return `hard-exit ${exitWorkflow.hardExitCount}건 · 미분류 0건`;
}

function positionCount(data: DashboardData) {
  return data.portfolio?.userState?.assetPositions?.length ?? 0;
}

function openOrderCount(data: DashboardData) {
  return data.portfolio?.openOrders?.length ?? 0;
}

function freshAlertCount(data: DashboardData) {
  const alerts = data.alertCandidates?.alerts ?? [];
  return alerts.filter((alert) => alert.isFresh !== false).length;
}

function primaryFreshAlert(data: DashboardData) {
  const alerts = data.alertCandidates?.alerts ?? [];
  return alerts
    .filter((alert) => alert.isFresh !== false)
    .sort((left, right) => right.candidateScore - left.candidateScore)[0];
}

function staleCandidateCount(data: DashboardData) {
  return data.alertCandidates?.staleOmitted ?? 0;
}

function candidateFactLabels(freshCount: number, staleCount: number) {
  if (staleCount > 0) return [`검토할 소식 ${freshCount}개`, `참고용 지난 소식 ${staleCount}개`];
  return [`검토할 소식 ${freshCount}개`];
}

export function summarizeFlatReadiness(
  data: DashboardData,
  cycle?: CycleOperatorSummary | null,
): FlatReadinessSummary {
  const positions = positionCount(data);
  const openOrders = openOrderCount(data);
  const delegate = data.executionDelegate?.executionDelegate;
  const candidateCount = freshAlertCount(data);
  const staleCandidates = staleCandidateCount(data);
  const notes = (data.collaborationNotes?.notes ?? []).filter((note) => note.category !== "review");
  const recentVerdicts = notes.filter((note) => note.verdict?.decision);
  const withholds = recentVerdicts.filter(
    (note) => note.verdict?.decision?.toLowerCase() === "withhold",
  ).length;
  const actionableVerdicts = recentVerdicts.filter((note) =>
    ["enter", "close", "reduce", "add", "scout"].includes(
      note.verdict?.decision?.toLowerCase() ?? "",
    ),
  ).length;
  const sourceClasses = notes.flatMap((note) => {
    const sourceClass = note.entryContext?.sourceClass;
    return sourceClass ? [sourceClass] : [];
  });
  const weakSourceCount = sourceClasses.filter((sourceClass) =>
    WEAK_SOURCE_CLASSES.has(sourceClass),
  ).length;
  const recentExecutions = data.ledger?.recentExecutions?.length ?? 0;
  const inactiveSubmitted = (data.ledger?.recentExecutions ?? []).filter(
    (execution) =>
      execution.status === "submitted_no_fill_no_open_order" ||
      (execution.status === "submitted" &&
        execution.fillCount === 0 &&
        execution.openOrderCount === 0 &&
        positions === 0 &&
        openOrders === 0),
  ).length;
  const executionFact =
    inactiveSubmitted > 0
      ? `최근 제출 ${inactiveSubmitted}건은 현재 포지션/대기주문 없음`
      : `최근 실행 ${recentExecutions}건`;
  const primaryNewsLine =
    alertNewsLine(primaryFreshAlert(data)) ??
    noteNewsLine(recentVerdicts.find((note) => note.entryContext?.assets?.length));
  const learningFact = learningReviewFact(data);
  const exitPlanFact = exitPlanQualityFact(data);
  const exitFlowFact = exitWorkflowFact(data);
  const bundle20ExitFact = bundle20ExitEnforceFact(cycle);

  if (positions > 0 || openOrders > 0) {
    return {
      tone: "green",
      title: "노출 있음",
      detail: `포지션 ${positions}개 · 대기 주문 ${openOrders}개`,
      facts: appendOperationalFacts(
        [
          delegate ? "실행 담당 있음" : "실행 담당 없음",
          executionFact,
          primaryNewsLine ?? candidateFactLabels(candidateCount, staleCandidates)[0],
        ],
        exitFlowFact,
        exitPlanFact,
        bundle20ExitFact,
        learningFact,
      ),
    };
  }

  if (!delegate) {
    return {
      tone: "red",
      title: "실행 담당 없음",
      detail: "실행 권한자가 없으면 좋은 소식이 와도 제출 경로가 막힙니다.",
      facts: appendOperationalFacts(
        [
          primaryNewsLine ?? candidateFactLabels(candidateCount, staleCandidates)[0],
          `최근 판단 ${recentVerdicts.length}건`,
        ],
        exitFlowFact,
        exitPlanFact,
        bundle20ExitFact,
        learningFact,
      ),
    };
  }

  if (candidateCount === 0 && recentVerdicts.length > 0 && withholds === recentVerdicts.length) {
    const qualityLine = compactSourceClasses(sourceClasses);
    return {
      tone: weakSourceCount > 0 ? "amber" : "blue",
      title: "최근 판단 보류",
      detail: primaryNewsLine ?? `검토할 소식이 없고 최근 판단 ${withholds}건은 보류였습니다.`,
      facts: appendOperationalFacts(
        ["실행 담당 있음", candidateFactLabels(candidateCount, staleCandidates)[0], qualityLine],
        exitFlowFact,
        exitPlanFact,
        bundle20ExitFact,
        learningFact,
      ),
    };
  }

  if (candidateCount > 0 && actionableVerdicts === 0) {
    return {
      tone: "amber",
      title: "소식 검토 중",
      detail: primaryNewsLine ?? `거래 판단할 소식 ${candidateCount}개를 확인 중입니다.`,
      facts: appendOperationalFacts(
        [
          "실행 담당 있음",
          `최근 판단 ${recentVerdicts.length}건`,
          compactSourceClasses(sourceClasses),
        ],
        exitFlowFact,
        exitPlanFact,
        bundle20ExitFact,
        learningFact,
      ),
    };
  }

  return {
    tone: "blue",
    title: "포지션 없음",
    detail: primaryNewsLine ?? "포지션/주문 없음.",
    facts: appendOperationalFacts(
      ["실행 담당 있음", candidateFactLabels(candidateCount, staleCandidates)[0], executionFact],
      exitFlowFact,
      exitPlanFact,
      bundle20ExitFact,
      learningFact,
    ),
  };
}
